import {
  OAuthProviderType,
  ServerDeploymentType,
  ServerFeature,
} from '@affine/graphql';

import {
  type ServerConfig,
  type ServerMetadata,
  CloudProvider,
  EApiPrefix,
} from './types';

// add strict key types to make index access type-safe
type EnvKey =
  | EApiPrefix.PROD
  | EApiPrefix.PRODUCTION
  | EApiPrefix.DEV
  | EApiPrefix.DEVELOPMENT;

type ProviderKey = CloudProvider.ALIYUN | CloudProvider.TENCENT;

const baseUrlMap: Record<EnvKey, Record<ProviderKey, { api: string }>> = {
  [EApiPrefix.PROD]: {
    [CloudProvider.ALIYUN]: {
      api: 'https://api-va.muset.ai',
    },
    [CloudProvider.TENCENT]: {
      api: 'https://api.muset.ai',
    },
  },
  [EApiPrefix.PRODUCTION]: {
    [CloudProvider.ALIYUN]: {
      api: 'https://api-va.muset.ai',
    },
    [CloudProvider.TENCENT]: {
      api: 'https://api.muset.ai',
    },
  },
  // 开发环境
  [EApiPrefix.DEV]: {
    [CloudProvider.ALIYUN]: {
      api: 'https://muset-va-dev.wenxiaobai.com',
    },
    [CloudProvider.TENCENT]: {
      api: 'https://muset-va-dev.wenxiaobai.com',
    },
  },
  [EApiPrefix.DEVELOPMENT]: {
    [CloudProvider.ALIYUN]: {
      api: 'https://muset-va-dev.wenxiaobai.com',
    },
    [CloudProvider.TENCENT]: {
      api: 'https://muset-va-dev.wenxiaobai.com',
    },
  },
};
const chatBaseUrlMap: Record<EnvKey, Record<ProviderKey, { api: string }>> = {
  [EApiPrefix.PROD]: {
    [CloudProvider.ALIYUN]: {
      api: 'api-va.muset.ai',
    },
    [CloudProvider.TENCENT]: {
      api: 'api.muset.ai',
    },
  },
  [EApiPrefix.PRODUCTION]: {
    [CloudProvider.ALIYUN]: {
      api: 'api-va.muset.ai',
    },
    [CloudProvider.TENCENT]: {
      api: 'api.muset.ai',
    },
  },
  // 开发环境
  [EApiPrefix.DEV]: {
    [CloudProvider.ALIYUN]: {
      api: 'muset-va-dev.wenxiaobai.com',
    },
    [CloudProvider.TENCENT]: {
      api: 'muset-va-dev.wenxiaobai.com',
    },
  },
  [EApiPrefix.DEVELOPMENT]: {
    [CloudProvider.ALIYUN]: {
      api: 'muset-va-dev.wenxiaobai.com',
    },
    [CloudProvider.TENCENT]: {
      api: 'muset-va-dev.wenxiaobai.com',
    },
  },
};

// 自定义环境配置
export const getCustomApiBaseUrl = () => {
  // 从环境变量获取自定义API环境
  const customEnv = (process.env.CUSTOM_API_ENV || 'development') as EnvKey;
  let cloudProvider = process.env.CLOUD_PROVIDER as ProviderKey;
  if (!cloudProvider) cloudProvider = 'TENCENT' as ProviderKey;
  return baseUrlMap[customEnv][cloudProvider].api;
};

export const getChatCustomApiBaseUrl = () => {
  const customEnv = (process.env.CUSTOM_API_ENV || 'development') as EnvKey;
  let cloudProvider = process.env.CLOUD_PROVIDER as ProviderKey;
  if (!cloudProvider) cloudProvider = 'TENCENT' as ProviderKey;
  return chatBaseUrlMap[customEnv][cloudProvider].api;
};
export const getCloud = () => {
  return process.env.CLOUD_PROVIDER || 'TENCENT';
};

export const getEnv = () => {
  // 从环境变量获取自定义API环境
  const customEnv = process.env.CUSTOM_API_ENV || 'development';

  switch (customEnv) {
    case 'prod':
    case 'production':
      return 'prod';
    case 'dev':
    case 'development':
    default:
      return 'dev';
  }
};

export const BUILD_IN_SERVERS: (ServerMetadata & { config: ServerConfig })[] =
  environment.isSelfHosted
    ? [
        {
          id: 'affine-cloud',
          baseUrl: getCustomApiBaseUrl(),
          // selfhosted baseUrl is `location.origin`
          // this is ok for web app, but not for desktop app
          // since we never build desktop app in selfhosted mode, so it's fine
          config: {
            serverName: 'Affine Selfhost',
            features: [ServerFeature.LocalWorkspace],
            oauthProviders: [],
            type: ServerDeploymentType.Selfhosted,
            credentialsRequirement: {
              password: {
                minLength: 8,
                maxLength: 32,
              },
            },
          },
        },
      ]
    : BUILD_CONFIG.debug
      ? [
          {
            id: 'affine-cloud',
            baseUrl: getCustomApiBaseUrl(), // 桌面端也使用自定义API地址
            config: {
              serverName: 'Affine Cloud',
              features: [
                ServerFeature.Indexer,
                ServerFeature.Copilot,
                ServerFeature.CopilotEmbedding,
                ServerFeature.OAuth,
                ServerFeature.Payment,
                ServerFeature.LocalWorkspace,
              ],
              oauthProviders: [
                OAuthProviderType.Google,
                OAuthProviderType.Apple,
              ],
              type: ServerDeploymentType.Affine,
              credentialsRequirement: {
                password: {
                  minLength: 8,
                  maxLength: 32,
                },
              },
            },
          },
        ]
      : BUILD_CONFIG.appBuildType === 'stable'
        ? [
            {
              id: 'affine-cloud',
              baseUrl: getCustomApiBaseUrl(),
              config: {
                serverName: 'Affine Cloud',
                features: [
                  ServerFeature.Indexer,
                  ServerFeature.Copilot,
                  ServerFeature.CopilotEmbedding,
                  ServerFeature.OAuth,
                  ServerFeature.Payment,
                  ServerFeature.LocalWorkspace,
                ],
                oauthProviders: [
                  OAuthProviderType.Google,
                  OAuthProviderType.Apple,
                ],
                type: ServerDeploymentType.Affine,
                credentialsRequirement: {
                  password: {
                    minLength: 8,
                    maxLength: 32,
                  },
                },
              },
            },
          ]
        : BUILD_CONFIG.appBuildType === 'beta'
          ? [
              {
                id: 'affine-cloud',
                baseUrl: getCustomApiBaseUrl(),
                config: {
                  serverName: 'Affine Cloud',
                  features: [
                    ServerFeature.Indexer,
                    ServerFeature.Copilot,
                    ServerFeature.CopilotEmbedding,
                    ServerFeature.OAuth,
                    ServerFeature.Payment,
                    ServerFeature.LocalWorkspace,
                  ],
                  oauthProviders: [
                    OAuthProviderType.Google,
                    OAuthProviderType.Apple,
                  ],
                  type: ServerDeploymentType.Affine,
                  credentialsRequirement: {
                    password: {
                      minLength: 8,
                      maxLength: 32,
                    },
                  },
                },
              },
            ]
          : BUILD_CONFIG.appBuildType === 'internal'
            ? [
                {
                  id: 'affine-cloud',
                  baseUrl: getCustomApiBaseUrl(),
                  config: {
                    serverName: 'Affine Cloud',
                    features: [
                      ServerFeature.Indexer,
                      ServerFeature.Copilot,
                      ServerFeature.CopilotEmbedding,
                      ServerFeature.OAuth,
                      ServerFeature.Payment,
                      ServerFeature.LocalWorkspace,
                    ],
                    oauthProviders: [
                      OAuthProviderType.Google,
                      OAuthProviderType.Apple,
                    ],
                    type: ServerDeploymentType.Affine,
                    credentialsRequirement: {
                      password: {
                        minLength: 8,
                        maxLength: 32,
                      },
                    },
                  },
                },
              ]
            : BUILD_CONFIG.appBuildType === 'canary'
              ? [
                  {
                    id: 'affine-cloud',
                    baseUrl: getCustomApiBaseUrl(),
                    config: {
                      serverName: 'Affine Cloud',
                      features: [
                        ServerFeature.Indexer,
                        ServerFeature.Copilot,
                        ServerFeature.CopilotEmbedding,
                        ServerFeature.OAuth,
                        ServerFeature.Payment,
                        ServerFeature.LocalWorkspace,
                      ],
                      oauthProviders: [
                        OAuthProviderType.Google,
                        OAuthProviderType.Apple,
                      ],
                      type: ServerDeploymentType.Affine,
                      credentialsRequirement: {
                        password: {
                          minLength: 8,
                          maxLength: 32,
                        },
                      },
                    },
                  },
                ]
              : [];
